import { useState, useMemo, useRef } from 'react';

import { HereLayer } from 'common/utils/MapMarker/types';
import { MarkerServicePriceStation } from 'entities/stations/types/store';

import { svgToDataURL } from 'common/utils';
import {
  useMarkerServiceStations,
  useBestRouteStationCodesMap,
  useCheckIsSelectedStationOnRoute
} from 'entities/stations';

import commonStationIcon from 'features/stations/StationLocations/assets/mapIcons/stations/common/default.png';

type ClusterId = number;
type Service = {
  name: string;
  price: { amount: number; currency: string };
  priceType: string;
};
interface ClustersPriceMap {
  [key: ClusterId]: Service;
}

interface ClusterData {
  [key: string]: any[];
}

const estimateTextWidth = (text: string, fontWeight: 'normal' | 'bold' = 'normal') => {
  const averageCharWidth = fontWeight === 'bold' ? 6.4 : 5.8;

  return Math.ceil(text.length * averageCharWidth);
};

const getPopup = (name: string, value: string) => {
  const label = `${name}:`;
  const popupHeight = 72;
  const rectX = 16;
  const rectY = 13;
  const rectHeight = 30;
  const horizontalPadding = 8;
  const textGap = label.length > 5 ? -5 : 0;
  const labelX = rectX + horizontalPadding;
  const labelWidth = estimateTextWidth(label, 'bold');
  const valueX = labelX + labelWidth + textGap;
  const valueWidth = estimateTextWidth(value);
  const rectWidth = Math.max(80, valueX - rectX + valueWidth + horizontalPadding);
  const popupWidth = rectWidth + rectX * 2;
  const centerX = rectX + rectWidth / 2;
  const arrowTopY = rectY + rectHeight;
  const arrowBottomY = 51;
  const arrowHalfWidth = 14;
  const arrowInnerHalfWidth = 6;

  return {
    width: popupWidth,
    height: popupHeight,
    svg: `<svg width="${popupWidth}" height="${popupHeight}" viewBox="0 0 ${popupWidth} ${popupHeight}" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g filter="url(#filter0_ddd_2012_6)">
    <rect x="${rectX}" y="${rectY}" width="${rectWidth}" height="${rectHeight}" rx="8" fill="white"/>
    <text fill="#30343B" xml:space="preserve" style="white-space: pre" font-family="sans-serif" font-size="10" font-weight="bold" letter-spacing="0.004em"><tspan x="${labelX}" y="31.418">${label}</tspan></text>
    <text fill="#30343B" xml:space="preserve" style="white-space: pre" font-family="sans-serif" font-size="10" letter-spacing="0.004em"><tspan x="${valueX}" y="31.418">${value}</tspan></text>
    <path d="M${centerX + arrowHalfWidth} ${arrowTopY}H${centerX - arrowHalfWidth}L${centerX - arrowInnerHalfWidth} ${arrowTopY}L${centerX} ${arrowBottomY}L${centerX + arrowInnerHalfWidth} ${arrowTopY}H${centerX + arrowHalfWidth}Z" fill="white"/>
    </g>
    <defs>
    <filter id="filter0_ddd_2012_6" x="0" y="0" width="${popupWidth}" height="${popupHeight}" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
    <feOffset dy="5"/>
    <feGaussianBlur stdDeviation="3"/>
    <feComposite in2="hardAlpha" operator="out"/>
    <feColorMatrix type="matrix" values="0 0 0 0 0.388235 0 0 0 0 0.447059 0 0 0 0 0.509804 0 0 0 0.04 0"/>
    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2012_6"/>
    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
    <feOffset dy="3"/>
    <feGaussianBlur stdDeviation="8"/>
    <feComposite in2="hardAlpha" operator="out"/>
    <feColorMatrix type="matrix" values="0 0 0 0 0.388235 0 0 0 0 0.447059 0 0 0 0 0.509804 0 0 0 0.1 0"/>
    <feBlend mode="normal" in2="effect1_dropShadow_2012_6" result="effect2_dropShadow_2012_6"/>
    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
    <feOffset dy="9"/>
    <feGaussianBlur stdDeviation="6"/>
    <feComposite in2="hardAlpha" operator="out"/>
    <feColorMatrix type="matrix" values="0 0 0 0 0.388235 0 0 0 0 0.447059 0 0 0 0 0.509804 0 0 0 0.12 0"/>
    <feBlend mode="normal" in2="effect2_dropShadow_2012_6" result="effect3_dropShadow_2012_6"/>
    <feBlend mode="normal" in="SourceGraphic" in2="effect3_dropShadow_2012_6" result="shape"/>
    </filter>
    </defs>
    </svg>`
  };
};


const getClustersPriceMap = (
  clusteredData: ClusterData,
  bestRouteStationCodesMap: { [StationCode: string]: boolean; }
) => {
  const result = Object.values(clusteredData).reduce((acc, clustersAndStationsOnZoom) => {
    clustersAndStationsOnZoom.forEach((item: any) => {
      if (item.clusterId) {
        let hasBestPriceStation = false;

        const service = item.children.reduce((serviceAcc: null | Service, { data }: any) => {
          if (bestRouteStationCodesMap[data.stationCode]) {
            hasBestPriceStation = true;
          }

          if (data.services && data.services.length) {
            if (!serviceAcc) {
              return data.services[0];
            } else {
              return serviceAcc.price.amount > data.services[0].price.amount
                ? data.services[0]
                : serviceAcc;
            }
          }

          return serviceAcc;
        }, null as (null | Service));

        if (service && !hasBestPriceStation) {
          acc[item.clusterId] = service;
        }
      }
    });

    return acc;
  }, {} as ClustersPriceMap);

  return Object.keys(result).length ? result : undefined;
};

export const useStationPriceMarkerLayers = () => {
  const markerServiceStations = useMarkerServiceStations();
  const [clustersPriceMap, setClustersPriceMap] = useState<ClustersPriceMap | undefined>();
  const updateKeyRef = useRef<string>('');
  const bestRouteStationCodesMap = useBestRouteStationCodesMap();
  const checkIsSelectedStationOnRoute = useCheckIsSelectedStationOnRoute();

  const filteredMarkerServiceStations = useMemo(() => (
    (markerServiceStations || []).filter(({ stationCode }) => !checkIsSelectedStationOnRoute(stationCode))
  ), [markerServiceStations, checkIsSelectedStationOnRoute]);

  const hereStationMarkersLayer = useMemo(
    (): HereLayer => ({
      data: filteredMarkerServiceStations,
      name: 'stations-price',
      type: 'cluster',
      getCoordinates: ({ data }: { data: MarkerServicePriceStation | { coordinates: { latitude: number; longitude: number; } } }) => ({
        latitude: data.coordinates.latitude,
        longitude: data.coordinates.longitude
      }),
      getIcon: ({ clusterId, data }: any) => {
        const ratio = 2.5;

        if (clusterId && clustersPriceMap && clustersPriceMap[clusterId]) {
          const { name, price: { amount, currency } } = clustersPriceMap[clusterId];
          const popup = getPopup(name, `${amount} ${currency}`);

          return ({
            url: svgToDataURL(popup.svg),
            height: popup.height * ratio,
            width: popup.width * ratio,
            anchorY: popup.height * 3,
            anchorX: popup.width * ratio * 0.5,
            id: `${clusterId}-${amount}-${currency}`
          });
        }

        if (data && data.services.length && !bestRouteStationCodesMap[data.stationCode]) {
          const { name, price: { amount, currency } } = data.services[0];
          const popup = getPopup(name, `${amount} ${currency}`);

          return ({
            url: svgToDataURL(popup.svg),
            height: popup.height * ratio,
            width: popup.width * ratio,
            anchorY: popup.height * 3,
            anchorX: popup.width * ratio * 0.5,
            id: `${data.stationCode}-${amount}-${currency}-route-station-price`
          });
        }

        return ({
          url: commonStationIcon,
          height: 1,
          width: 1,
          anchorY: 0,
          id: '1'
        });
      },
      onClusteredData: (clusteredData: ClusterData) => {
        // Create and check updateKey for the update clustersPriceMap
        // Create updateKey by the 10 zoom level
        const newUpdateKey = clusteredData[10].reduce((acc, { clusterId }) => {
          if (clusterId) {
            acc += `${clusterId}-`;
          }
          return acc;
        }, '');

        if (newUpdateKey === '') {
          setClustersPriceMap(undefined);
          updateKeyRef.current = newUpdateKey;
        } else if (updateKeyRef.current !== newUpdateKey) {
          setClustersPriceMap(getClustersPriceMap(clusteredData, bestRouteStationCodesMap));
          updateKeyRef.current = newUpdateKey;
        }
      }
    }),
    [
      filteredMarkerServiceStations,
      setClustersPriceMap,
      clustersPriceMap,
      updateKeyRef,
      bestRouteStationCodesMap
    ]
  );

  return hereStationMarkersLayer;
};
